package dev.lingvo.site.ds

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.remember
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.attr
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderBottom
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.minWidth
import com.varabyte.kobweb.compose.ui.modifiers.outline
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.selectors.disabled
import com.varabyte.kobweb.silk.style.selectors.focusVisible
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import dev.lingvo.site.i18n.t
import kotlin.js.json
import kotlin.random.Random
import kotlinx.browser.document
import org.jetbrains.compose.web.attributes.ButtonType
import org.jetbrains.compose.web.attributes.disabled
import org.jetbrains.compose.web.attributes.type
import org.jetbrains.compose.web.css.CSSMediaQuery
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.HTMLElement

private val FINE_POINTER_QUERY = CSSMediaQuery.Raw("(hover: hover) and (pointer: fine)")

class LTab(
    val id: String,
    val label: String,
    val enabled: Boolean = true,
    val content: @Composable () -> Unit,
)

data class LSegmentedOption<T>(
    val value: T,
    val label: String,
    val enabled: Boolean = true,
)

val LTabsStyle = CssStyle.base {
    Modifier.gap(Space.LG)
}

/**
 * The rule beneath the strip, carried by a frame rather than by the strip itself.
 *
 * One element cannot do both jobs. [LTabListStyle] must clip — see its note — and a scroll container
 * clips at its **padding edge**, while the rule is drawn on the border, one step further out. That
 * is exactly where the selected tab has to reach: its 2px accent sits *on* the rule, overlapping it
 * via a negative bottom margin, which is what makes the tab look joined to the line rather than
 * hovering above it.
 *
 * So the accent was being drawn 1px outside the clip and shaved to 1px — measured, not inferred —
 * leaving the one mark that says which tab you are on rendering at the same weight as the hairline
 * it was meant to stand out against. A focus ring, offset 2px on every side, lost its top and bottom
 * strokes the same way and appeared as two floating bars.
 *
 * Splitting the two elements settles it for one `div`. The rule lives out here where nothing clips
 * it, and the strip is free to give itself room.
 */
val LTabListFrameStyle = CssStyle.base {
    Modifier
        .fillMaxWidth()
        .minWidth(0.px)
        .borderBottom(1.px, LineStyle.Solid, colorMode.toSitePalette().border)
}

val LTabListStyle = CssStyle.base {
    Modifier
        .fillMaxWidth()
        .minWidth(0.px)
        .gap(Space.XXL)
        .styleModifier {
            property("overflow-x", "auto")
            // Not a free choice. CSS resolves a `visible` cross axis to `auto` the moment the other
            // axis scrolls, so leaving this alone grows a vertical scrollbar on a strip one control
            // tall. Every tab strip pins it, and pinning it is what makes the padding below
            // load-bearing rather than cosmetic: from here on this element clips its children.
            property("overflow-y", "hidden")
            property("overscroll-behavior-inline", "contain")
            property("scrollbar-width", "thin")
            // Room inside the clip for the two things a tab draws outside its own box: a focus ring
            // reaching 4px above, and the selected accent reaching 1px below onto the rule. The
            // negative margins hand the same space straight back to the layout, so the strip
            // occupies exactly the height it did before and nothing on the page moves.
            property("padding-top", "4px")
            property("padding-bottom", "5px")
            property("margin-top", "-4px")
            property("margin-bottom", "-5px")
        }
}

val LTabStyle = CssStyle {
    base {
        Modifier
            .backgroundColor(com.varabyte.kobweb.compose.ui.graphics.Colors.Transparent)
            .color(colorMode.toSitePalette().textSecondary)
            .border(0.px, LineStyle.Solid, com.varabyte.kobweb.compose.ui.graphics.Colors.Transparent)
            .padding(topBottom = Space.MD)
            .fontSize(TypeScale.BodySmall)
            .fontWeight(600)
            .cursor(Cursor.Pointer)
            .styleModifier {
                property("margin-bottom", "-1px")
                property("flex", "0 0 auto")
                property("white-space", "nowrap")
            }
            // The transition belongs to the element, not to the hover rule. A `transition` declared
            // only inside `:hover` is gone the moment the pointer leaves — the rule stops matching,
            // the computed `transition-property` reverts to `none`, and the colour snaps back. The
            // control then eases in and jumps out, which reads as a glitch rather than as motion.
            // The media query decides which colours change; it must not decide whether a change is
            // transitionable.
            .transitionColors(durationMs = Motion.FastMs)
    }
    cssRule(FINE_POINTER_QUERY, ":not(:disabled):hover") {
        Modifier
            .color(colorMode.toSitePalette().textPrimary)
            // Restated rather than removed: the hover rule is what a reader of this file looks at to
            // see what hovering costs, and the declaration here is identical to the base one, so the
            // two cannot disagree.
            .transitionColors(durationMs = Motion.FastMs)
    }
    focusVisible {
        Modifier
            .outline(2.px, LineStyle.Solid, colorMode.toSitePalette().accent)
            .styleModifier { property("outline-offset", "2px") }
    }
    disabled {
        Modifier
            .color(colorMode.toSitePalette().textMuted)
            .cursor(Cursor.NotAllowed)
    }
}

val LSegmentedStyle = CssStyle.base {
    Modifier
        .backgroundColor(colorMode.toSitePalette().surfaceSunken)
        .border(1.px, LineStyle.Solid, colorMode.toSitePalette().border)
        .borderRadius(Radius.Control)
        .padding(Space.XXS)
        .gap(Space.XXS)
}

val LSegmentedButtonStyle = CssStyle {
    base {
        Modifier
            .backgroundColor(com.varabyte.kobweb.compose.ui.graphics.Colors.Transparent)
            .color(colorMode.toSitePalette().textSecondary)
            .border(1.px, LineStyle.Solid, com.varabyte.kobweb.compose.ui.graphics.Colors.Transparent)
            .borderRadius(Radius.Control)
            // 40, not the 34 that padding alone produced. The design system names 34 as its worked
            // example of a control that "reads fine and fails anyway", and this was one: the pill
            // looked right and was six pixels short of the hit-area floor. The container's padding
            // does not count towards it — that height is shared between the segments, so it belongs
            // to none of them.
            .height(ControlHeight.MD)
            .padding(topBottom = 0.px, leftRight = Space.MD)
            .fontSize(TypeScale.Small)
            .fontWeight(600)
            .cursor(Cursor.Pointer)
            // Symmetry, for the reason [LTabStyle] states: a transition living only in the hover
            // rule animates the way in and snaps the way out. The selected segment opts out of this
            // by hand with `noTransition()`, which is a decision about selection, not about hover.
            .transitionColors(durationMs = Motion.FastMs)
    }
    cssRule(FINE_POINTER_QUERY, ":not(:disabled):hover") {
        Modifier
            .backgroundColor(colorMode.toSitePalette().surfaceRaised)
            .transitionColors(durationMs = Motion.FastMs)
    }
    focusVisible {
        Modifier
            .outline(2.px, LineStyle.Solid, colorMode.toSitePalette().accent)
            .styleModifier { property("outline-offset", "2px") }
    }
    disabled {
        Modifier
            .color(colorMode.toSitePalette().textMuted)
            .cursor(Cursor.NotAllowed)
    }
}

@Composable
fun LTabs(
    tabs: List<LTab>,
    selectedId: String,
    onSelected: (String) -> Unit,
    modifier: Modifier = Modifier,
    label: String = t("ds.ds.lselection.tabs", "Tabs"),
) {
    require(tabs.isNotEmpty()) { "LTabs requires at least one tab" }
    val selected = tabs.firstOrNull { it.id == selectedId && it.enabled }
        ?: tabs.firstOrNull { it.enabled }
        ?: error("LTabs requires at least one enabled tab")
    val baseId = remember { "lingvo-tabs-${Random.nextInt(1_000_000)}" }

    // **The selected tab is never the one you cannot see.**
    //
    // [LTabListStyle] scrolls, and it carries no cut glyph, fade or arrow — deliberately, because a
    // permanent edge mask would dim the last tab of every strip that fits. The cost was that a
    // strip which *has* overflowed reads as the complete set: at 390 the seven-tab project settings
    // strip showed four, and `?tab=providers` or `?tab=qa` selected a tab three positions past the
    // right edge, so the page's own heading named a destination the strip appeared not to offer.
    //
    // Revealing the selection is the affordance: the reader sees the strip is scrolled, which says
    // there is more of it, and the tab they are on is on screen where the accent under it can be
    // read. `block: "nearest"` keeps this to the strip's inline axis — without it the same call
    // scrolls the page vertically on mount. `behavior: "auto"` is an instant adjustment rather than
    // motion: nothing animates, so there is no reduced-motion preference to answer here.
    LaunchedEffect(selected.id) {
        val tab = document.getElementById("$baseId-tab-${selected.id}") as? HTMLElement
        tab?.asDynamic()?.scrollIntoView(
            json("block" to "nearest", "inline" to "nearest", "behavior" to "auto"),
        )
    }

    Column(
        LTabsStyle.toModifier()
            .then(modifier)
            .fillMaxWidth()
            .minWidth(0.px)
            .styleModifier {
                property("width", "100%")
                property("min-width", "0px")
            }
            .attr("data-ltabs", "true"),
    ) {
        // The frame is a flex container, not a bare block: flex items' margins never collapse, and
        // the strip's negative margins are the whole mechanism holding the rule against the tabs.
        Column(LTabListFrameStyle.toModifier()) {
            Row(
                LTabListStyle.toModifier()
                    .attr("role", "tablist")
                    .attr("aria-label", label),
            ) {
                tabs.forEachIndexed { index, tab ->
                    val isSelected = tab.id == selected.id
                    Button(
                        LTabStyle.toModifier()
                            .then(
                                if (isSelected) {
                                    Modifier
                                        .color(ColorMode.current.toSitePalette().textPrimary)
                                        .borderBottom(2.px, LineStyle.Solid, ColorMode.current.toSitePalette().accent)
                                } else {
                                    Modifier
                                },
                            )
                            .then(if (isSelected) Modifier.noTransition() else Modifier)
                            .toAttrs {
                                id("$baseId-tab-${tab.id}")
                                type(ButtonType.Button)
                                attr("role", "tab")
                                attr("aria-selected", isSelected.toString())
                                attr("data-state", if (isSelected) "selected" else "unselected")
                                attr("aria-controls", "$baseId-panel-${tab.id}")
                                tabIndex(if (isSelected) 0 else -1)
                                if (!tab.enabled) disabled()
                                onClick { onSelected(tab.id) }
                                onKeyDown { event ->
                                    val targetIndex = tabKeyTarget(event.key, index, tabs.map { it.enabled })
                                    if (targetIndex != null) {
                                        event.preventDefault()
                                        val target = tabs[targetIndex]
                                        onSelected(target.id)
                                        (document.getElementById("$baseId-tab-${target.id}") as? HTMLElement)?.focus()
                                    }
                                }
                            },
                    ) {
                        Text(tab.label)
                    }
                }
            }
        }
        Div(
            Modifier
                .fillMaxWidth()
                .minWidth(0.px)
                .styleModifier {
                    property("width", "100%")
                    property("min-width", "0px")
                }
                .attr("data-ltabs-panel", "true")
                .toAttrs {
                    id("$baseId-panel-${selected.id}")
                    attr("role", "tabpanel")
                    attr("aria-labelledby", "$baseId-tab-${selected.id}")
                    tabIndex(0)
                },
        ) {
            selected.content()
        }
    }
}

@Composable
fun <T> LSegmented(
    options: List<LSegmentedOption<T>>,
    selected: T,
    onSelected: (T) -> Unit,
    label: String,
    modifier: Modifier = Modifier,
) {
    require(options.isNotEmpty()) { "LSegmented requires at least one option" }
    val selectedIndex = options.indexOfFirst { it.value == selected && it.enabled }
        .takeIf { it >= 0 }
        ?: options.indexOfFirst { it.enabled }
            .takeIf { it >= 0 }
        ?: error("LSegmented requires at least one enabled option")
    val baseId = remember { "lingvo-segmented-${Random.nextInt(1_000_000)}" }
    Row(
        LSegmentedStyle.toModifier()
            .then(modifier)
            .attr("role", "radiogroup")
            .attr("aria-label", label),
    ) {
        options.forEachIndexed { index, option ->
            val isSelected = index == selectedIndex
            Button(
                LSegmentedButtonStyle.toModifier()
                    .then(
                        if (isSelected) {
                            Modifier
                                .backgroundColor(ColorMode.current.toSitePalette().accentSubtle)
                                .color(ColorMode.current.toSitePalette().accent)
                                .fontWeight(700)
                        } else {
                            Modifier
                        },
                    )
                    .then(if (isSelected) Modifier.noTransition() else Modifier)
                    .toAttrs {
                        id("$baseId-$index")
                        type(ButtonType.Button)
                        attr("role", "radio")
                        attr("aria-checked", isSelected.toString())
                        attr("data-state", if (isSelected) "selected" else "unselected")
                        tabIndex(if (isSelected) 0 else -1)
                        if (!option.enabled) disabled()
                        onClick { onSelected(option.value) }
                        onKeyDown { event ->
                            val targetIndex = tabKeyTarget(event.key, index, options.map { it.enabled })
                            if (targetIndex != null) {
                                event.preventDefault()
                                onSelected(options[targetIndex].value)
                                (document.getElementById("$baseId-$targetIndex") as? HTMLElement)?.focus()
                            }
                        }
                    },
            ) {
                Text(option.label)
            }
        }
    }
}

private fun tabKeyTarget(
    key: String,
    currentIndex: Int,
    enabled: List<Boolean>,
): Int? {
    val enabledIndices = enabled.indices.filter { enabled[it] }
    if (enabledIndices.isEmpty()) return null
    val currentEnabledIndex = enabledIndices.indexOf(currentIndex).coerceAtLeast(0)
    return when (key) {
        "ArrowRight", "ArrowDown" -> enabledIndices[(currentEnabledIndex + 1) % enabledIndices.size]
        "ArrowLeft", "ArrowUp" -> enabledIndices[(currentEnabledIndex - 1 + enabledIndices.size) % enabledIndices.size]
        "Home" -> enabledIndices.first()
        "End" -> enabledIndices.last()
        else -> null
    }
}
